import { DOCUMENT } from "@angular/common";
import { computed, Injectable, inject, signal } from "@angular/core";
import { silentSessionExpired } from "@interceptors/silent-request.token";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

const POLL_MS = 60_000;

/** A side-menu page whose count clears when the admin opens it. */
export type SeenPage = "vendors" | "users" | "messages" | "pending" | "products";

/**
 * What this admin last saw of each page, in this browser: when they opened it
 * (ms), or for products the newest product id then (products have no date
 * index - admin-backend nav-dots.controller).
 */
const SEEN_KEY: Record<SeenPage, string> = {
  vendors: "fh-admin-seen-vendors",
  users: "fh-admin-seen-users",
  messages: "fh-admin-seen-messages",
  pending: "fh-admin-seen-pending",
  products: "fh-admin-seen-product-id",
};

interface NavDotsData {
  newVendors: number;
  newUsers: number;
  newMessages: number;
  flaggedUnseen: number;
  inProgressOrders: number;
  newPendingOrders: number;
  newProducts: number;
  latestProductId: number | null;
}

function readSeen(page: SeenPage): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY[page])) || 0;
  } catch {
    return 0;
  }
}

function writeSeen(page: SeenPage, value: number): void {
  try {
    localStorage.setItem(SEEN_KEY[page], String(value));
  } catch {
    // Blocked storage: counted again from the last 7 days next time.
  }
}

/**
 * The side menu's numbered red dots (admin-backend nav-dots.controller):
 * - Vendor List, User List, Pending Orders List, Product List: new since this
 *   admin last opened that page (the last 7 days on a first visit here).
 * - Messages: shopper and store messages since the last visit, plus flagged
 *   conversations no admin has opened since.
 * - Orders List: orders from the last 7 days still In Progress.
 * Polled every 60 s while the page is visible, without the loader.
 */
@Injectable({ providedIn: "root" })
export class NavDotsService {
  private readonly requestService = inject(RequestService);
  private readonly document = inject(DOCUMENT);

  private readonly data = signal<NavDotsData | null>(null);

  readonly vendors = computed(() => this.data()?.newVendors ?? 0);
  readonly users = computed(() => this.data()?.newUsers ?? 0);
  /**
   * Shopper and store messages since the last visit. The flagged half of the Messages dot
   * comes from MessagesUnreadService, which the Messages page refreshes at once on review.
   */
  readonly newMessages = computed(() => this.data()?.newMessages ?? 0);
  readonly orders = computed(() => this.data()?.inProgressOrders ?? 0);
  readonly pending = computed(() => this.data()?.newPendingOrders ?? 0);
  readonly products = computed(() => this.data()?.newProducts ?? 0);

  /** Opened before the product count came back: remembered once it does. */
  private productsSeenPending = false;

  /**
   * When each page was last marked seen (ms): a refresh that was already in flight then
   * counted from the old mark, so its count for that page is dropped (see refresh()).
   */
  private readonly seenAt: Partial<Record<SeenPage, number>> = {};

  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly onVisibility = () => {
    if (!this.document.hidden) this.refresh();
  };

  start(): void {
    if (this.timer) return;
    this.refresh();
    this.timer = setInterval(() => {
      if (!this.document.hidden) this.refresh();
    }, POLL_MS);
    this.document.addEventListener("visibilitychange", this.onVisibility);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.document.removeEventListener("visibilitychange", this.onVisibility);
    this.data.set(null);
  }

  /** The admin opened one of the pages: its count starts again from now. */
  markSeen(page: SeenPage): void {
    this.seenAt[page] = Date.now();
    if (page === "products") {
      const latest = this.data()?.latestProductId;
      if (latest) writeSeen("products", latest);
      else this.productsSeenPending = true;
    } else {
      writeSeen(page, Date.now());
    }
    const d = this.data();
    if (d) {
      const cleared: Partial<NavDotsData> = {
        vendors: { newVendors: 0 },
        users: { newUsers: 0 },
        messages: { newMessages: 0 },
        pending: { newPendingOrders: 0 },
        products: { newProducts: 0 },
      }[page];
      this.data.set({ ...d, ...cleared });
    }
  }

  refresh(): void {
    // Session gone (a background poll was refused): stop asking until the next login.
    if (silentSessionExpired()) return;
    const since = (page: SeenPage) => {
      const ms = readSeen(page);
      return ms ? new Date(ms).toISOString() : "";
    };
    const params = new URLSearchParams({
      vendorsSince: since("vendors"),
      usersSince: since("users"),
      messagesSince: since("messages"),
      pendingSince: since("pending"),
      productsAfter: String(readSeen("products") || ""),
    });
    const startedAt = Date.now();
    const seenSinceStart = (page: SeenPage) => (this.seenAt[page] ?? 0) >= startedAt;
    this.requestService
      .get<IApiResponse>(`/api/v1/nav-dots?${params.toString()}`, { background: true })
      .subscribe({
        next: (res) => {
          if (!res?.status || !res.data) return;
          const d = res.data as NavDotsData;
          // Opened while this request was in flight: its counts predate that visit.
          if (seenSinceStart("vendors")) d.newVendors = 0;
          if (seenSinceStart("users")) d.newUsers = 0;
          if (seenSinceStart("messages")) d.newMessages = 0;
          if (seenSinceStart("pending")) d.newPendingOrders = 0;
          if ((this.productsSeenPending || seenSinceStart("products")) && d.latestProductId) {
            this.productsSeenPending = false;
            writeSeen("products", Math.max(readSeen("products"), d.latestProductId));
            d.newProducts = 0;
          } else if (seenSinceStart("products")) {
            d.newProducts = 0;
          }
          this.data.set(d);
        },
        error: () => {},
      });
  }
}
